import { ActionSchema } from "../actions";
import {
  GetSearchUsersSchema,
  EnabledFieldSettingsSchema,
  AddUserSchema,
  CustomerManagementConst,
  GetUserIdentitySchema,
  PostSessionsSchema,
  AddSecondaryEmailSchema,
  BlockOrUnblockUserSchema,
  DeleteUserSchema,
  VerifySchema,
  ResetPasswordSchema,
  RemoveEmailAddressSchema,
  UpdateUserProfileSchema,
  GetAssignedRoleSchema,
  AssignRolesSchema,
  UnassignRolesSchema,
  GetCustomObjectsSchema,
  GetCustomObjectDataSchema,
  GetRoleContextSchema,
  AddRoleContextSchema,
  DeleteRoleContextSchema,
  UnlockUserSchema,
  ResendEmailSchema,
  GetConsentLogsResponse,
  GetCustomFieldsSchema,
  GetESFieldsSchema,
  GetPrivacyPolicyHistorySchema,
  GetTwoFASettingsSchema,
  DeleteMFAFactorSchema,
  GetConsumerAuditLogsSchema,
  ResetPinSchema,
  GetConsumerAuditLogsV1Schema,
  SendResetPasswordSchema,
  GetMFAFactorsSchema
} from "../actions/customerManagementActions";
import { combineReducers } from "redux";
import { UserOrgs, UserOrgsState } from "../features/organizations/userOrgs";
import { getOutcomeTrackingState, getRequestTrackingState, MutationTrackingState } from "../../services/analytics/tracking";

const withRequestTracking = <T extends object>(state: T, action: ActionSchema<any>) => ({
  ...state,
  ...getRequestTrackingState(action as any)
});

const withSuccessTracking = <T extends object>(state: T, action: ActionSchema<any>) => ({
  ...state,
  ...action.payload,
  ...getOutcomeTrackingState(state as MutationTrackingState, "success")
});

const withErrorTracking = <T extends object>(state: T, action: ActionSchema<any>) => ({
  ...state,
  ...action.payload,
  ...getOutcomeTrackingState(state as MutationTrackingState, "error")
});

const userBySearchFieldsReducer = (state = {}, action: ActionSchema<GetSearchUsersSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.GET_USER_BY_SEARCHFIELDS_LOADING:
    case CustomerManagementConst.GET_USER_BY_SEARCHFIELDS_SUCCESS:
    case CustomerManagementConst.GET_USER_BY_SEARCHFIELDS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const usersReducer = (state = {}, action: ActionSchema<GetSearchUsersSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.GET_USERS_LOADING:
    case CustomerManagementConst.GET_USERS_SUCCESS:
    case CustomerManagementConst.GET_USERS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const enabledFieldSettingsReducer = (state = {}, action: ActionSchema<EnabledFieldSettingsSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.LOAD_ENABLED_FIELDS_SETTINGS_LOADING:
    case CustomerManagementConst.LOAD_ENABLED_FIELDS_SETTINGS_SUCCESS:
    case CustomerManagementConst.LOAD_ENABLED_FIELDS_SETTINGS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const addUserReducer = (state = {}, action: ActionSchema<AddUserSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.ADD_USER:
      return withRequestTracking(state, action);
    case CustomerManagementConst.ADD_USER_LOADING:
      return { ...state, ...action.payload };
    case CustomerManagementConst.ADD_USER_SUCCESS:
      return withSuccessTracking(state, action);
    case CustomerManagementConst.ADD_USER_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const userIdentityReducer = (state = {}, action: ActionSchema<GetUserIdentitySchema>) => {
  switch (action.type) {
    case CustomerManagementConst.GET_USER_IDENTITY_LOADING:
    case CustomerManagementConst.GET_USER_IDENTITY_SUCCESS:
    case CustomerManagementConst.GET_USER_IDENTITY_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const postSessionsReducer = (state = {}, action: ActionSchema<PostSessionsSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.POST_SESSIONS_LOADING:
    case CustomerManagementConst.POST_SESSIONS_SUCCESS:
    case CustomerManagementConst.POST_SESSIONS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const addSecondaryEmailReducer = (state = {}, action: ActionSchema<AddSecondaryEmailSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.ADD_SECONDARY_EMAIL_LOADING:
    case CustomerManagementConst.ADD_SECONDARY_EMAIL_SUCCESS:
    case CustomerManagementConst.ADD_SECONDARY_EMAIL_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const blockOrUnblockUserReducer = (state = {}, action: ActionSchema<BlockOrUnblockUserSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.BLOCK_OR_UNBLOCK_USER_LOADING:
    case CustomerManagementConst.BLOCK_OR_UNBLOCK_USER_SUCCESS:
    case CustomerManagementConst.BLOCK_OR_UNBLOCK_USER_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const deleteUserReducer = (state = {}, action: ActionSchema<DeleteUserSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.DELETE_USER_LOADING:
    case CustomerManagementConst.DELETE_USER_SUCCESS:
    case CustomerManagementConst.DELETE_USER_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const unlockUserReducer = (state = {}, action: ActionSchema<UnlockUserSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.UNLOCK_USER_LOADING:
    case CustomerManagementConst.UNLOCK_USER_SUCCESS:
    case CustomerManagementConst.UNLOCK_USER_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const verifyReducer = (state = {}, action: ActionSchema<VerifySchema>) => {
  switch (action.type) {
    case CustomerManagementConst.VERIFY_LOADING:
    case CustomerManagementConst.VERIFY_SUCCESS:
    case CustomerManagementConst.VERIFY_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const resetPasswordReducer = (state = {}, action: ActionSchema<ResetPasswordSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.RESET_PASSWORD_LOADING:
    case CustomerManagementConst.RESET_PASSWORD_SUCCESS:
    case CustomerManagementConst.RESET_PASSWORD_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const resetPinReducer = (state = {}, action: ActionSchema<ResetPinSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.RESET_PIN_LOADING:
    case CustomerManagementConst.RESET_PIN_SUCCESS:
    case CustomerManagementConst.RESET_PIN_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const sendResetPasswordReducer = (state = {}, action: ActionSchema<SendResetPasswordSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.SEND_RESET_PASSWORD_LOADING:
    case CustomerManagementConst.SEND_RESET_PASSWORD_SUCCESS:
    case CustomerManagementConst.SEND_RESET_PASSWORD_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const resendEmailReducer = (state = {}, action: ActionSchema<ResendEmailSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.RESEND_EMAIL_LOADING:
    case CustomerManagementConst.RESEND_EMAIL_SUCCESS:
    case CustomerManagementConst.RESEND_EMAIL_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const removeEmailAddressReducer = (state = {}, action: ActionSchema<RemoveEmailAddressSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.REMOVE_EMAIL_ADDRESS_LOADING:
    case CustomerManagementConst.REMOVE_EMAIL_ADDRESS_SUCCESS:
    case CustomerManagementConst.REMOVE_EMAIL_ADDRESS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const updateUserProfileReducer = (state = {}, action: ActionSchema<UpdateUserProfileSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.UPDATE_USER_PROFILE_LOADING:
    case CustomerManagementConst.UPDATE_USER_PROFILE_SUCCESS:
    case CustomerManagementConst.UPDATE_USER_PROFILE_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getAssignedRoleReducer = (state = {}, action: ActionSchema<GetAssignedRoleSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.GET_ASSIGNED_ROLE_LOADING:
    case CustomerManagementConst.GET_ASSIGNED_ROLE_SUCCESS:
    case CustomerManagementConst.GET_ASSIGNED_ROLE_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const assignRolesReducer = (state = {}, action: ActionSchema<AssignRolesSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.ASSIGN_ROLES_LOADING:
    case CustomerManagementConst.ASSIGN_ROLES_SUCCESS:
    case CustomerManagementConst.ASSIGN_ROLES_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const unassignRolesReducer = (state = {}, action: ActionSchema<UnassignRolesSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.UNASSIGN_ROLES_LOADING:
    case CustomerManagementConst.UNASSIGN_ROLES_SUCCESS:
    case CustomerManagementConst.UNASSIGN_ROLES_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getCustomObjectDataReducer = (state = {}, action: ActionSchema<GetCustomObjectDataSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.GET_CUSTOM_OBJECT_DATA_LOADING:
    case CustomerManagementConst.GET_CUSTOM_OBJECT_DATA_SUCCESS:
    case CustomerManagementConst.GET_CUSTOM_OBJECT_DATA_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getCustomObjectsReducer = (state = {}, action: ActionSchema<GetCustomObjectsSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.GET_CUSTOM_OBJECTS_LOADING:
    case CustomerManagementConst.GET_CUSTOM_OBJECTS_SUCCESS:
    case CustomerManagementConst.GET_CUSTOM_OBJECTS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getRoleContextReducer = (state = {}, action: ActionSchema<GetRoleContextSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.GET_ROLE_CONTEXT_LOADING:
    case CustomerManagementConst.GET_ROLE_CONTEXT_SUCCESS:
    case CustomerManagementConst.GET_ROLE_CONTEXT_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const addRoleContextReducer = (state = {}, action: ActionSchema<AddRoleContextSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.ADD_ROLE_CONTEXT_LOADING:
    case CustomerManagementConst.ADD_ROLE_CONTEXT_SUCCESS:
    case CustomerManagementConst.ADD_ROLE_CONTEXT_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const deleteRoleContextReducer = (state = {}, action: ActionSchema<DeleteRoleContextSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.DELETE_ROLE_CONTEXT_LOADING:
    case CustomerManagementConst.DELETE_ROLE_CONTEXT_SUCCESS:
    case CustomerManagementConst.DELETE_ROLE_CONTEXT_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getConsentLogsReducer = (state = {}, action: ActionSchema<GetConsentLogsResponse>) => {
  switch (action.type) {
    case CustomerManagementConst.GET_CONSENT_LOGS_LOADING:
    case CustomerManagementConst.GET_CONSENT_LOGS_SUCCESS:
    case CustomerManagementConst.GET_CONSENT_LOGS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getCustomFieldsReducer = (state = {}, action: ActionSchema<GetCustomFieldsSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.GET_CUSTOM_FIELDS_LOADING:
    case CustomerManagementConst.GET_CUSTOM_FIELDS_SUCCESS:
    case CustomerManagementConst.GET_CUSTOM_FIELDS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getESFieldsReducer = (state = {}, action: ActionSchema<GetESFieldsSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.GET_ES_FIELDS_LOADING:
    case CustomerManagementConst.GET_ES_FIELDS_SUCCESS:
    case CustomerManagementConst.GET_ES_FIELDS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getPrivacyPolicyHistoryReducer = (state = {}, action: ActionSchema<GetPrivacyPolicyHistorySchema>) => {
  switch (action.type) {
    case CustomerManagementConst.GET_USER_PRIVACY_POLICY_LOADING:
    case CustomerManagementConst.GET_USER_PRIVACY_POLICY_SUCCESS:
    case CustomerManagementConst.GET_USER_PRIVACY_POLICY_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};
const getTwoFASettingsReducer = (state = {}, action: ActionSchema<GetTwoFASettingsSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.GET_USER_2FA_SETTING_LOADING:
    case CustomerManagementConst.GET_USER_2FA_SETTING_SUCCESS:
    case CustomerManagementConst.GET_USER_2FA_SETTING_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const deleteMFAFactorReducer = (state = {}, action: ActionSchema<DeleteMFAFactorSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.DELETE_MFA_FACTOR_LOADING:
    case CustomerManagementConst.DELETE_MFA_FACTOR_SUCCESS:
    case CustomerManagementConst.DELETE_MFA_FACTOR_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getMFAFactorsReducer = (state = {}, action: ActionSchema<GetMFAFactorsSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.GET_MFA_FACTORS_LOADING:
    case CustomerManagementConst.GET_MFA_FACTORS_SUCCESS:
    case CustomerManagementConst.GET_MFA_FACTORS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const consumerAuditLogsReducer = (state = {}, action: ActionSchema<GetConsumerAuditLogsSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.GET_CONSUMER_AUDIT_LOGS_LOADING:
    case CustomerManagementConst.GET_CONSUMER_AUDIT_LOGS_SUCCESS:
    case CustomerManagementConst.GET_CONSUMER_AUDIT_LOGS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const consumerAuditLogsByUIDReducer = (state = {}, action: ActionSchema<GetConsumerAuditLogsSchema>) => {
  switch (action.type) {
    case CustomerManagementConst.GET_CONSUMER_AUDIT_LOGS_BY_UID_LOADING:
    case CustomerManagementConst.GET_CONSUMER_AUDIT_LOGS_BY_UID_SUCCESS:
    case CustomerManagementConst.GET_CONSUMER_AUDIT_LOGS_BY_UID_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const consumerAuditLogsV1Reducer = (state = {}, action: ActionSchema<GetConsumerAuditLogsV1Schema>) => {
  switch (action.type) {
    case CustomerManagementConst.GET_CONSUMER_AUDIT_LOGS_V1_LOADING:
    case CustomerManagementConst.GET_CONSUMER_AUDIT_LOGS_V1_SUCCESS:
    case CustomerManagementConst.GET_CONSUMER_AUDIT_LOGS_V1_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const consumerAuditLogsByUIDV1Reducer = (state = {}, action: ActionSchema<GetConsumerAuditLogsV1Schema>) => {
  switch (action.type) {
    case CustomerManagementConst.GET_CONSUMER_AUDIT_LOGS_BY_UID_V1_LOADING:
    case CustomerManagementConst.GET_CONSUMER_AUDIT_LOGS_BY_UID_V1_SUCCESS:
    case CustomerManagementConst.GET_CONSUMER_AUDIT_LOGS_BY_UID_V1_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

// Combine Reducers
const customerManagementReducer = combineReducers({
  userBySearchFields: userBySearchFieldsReducer,
  users: usersReducer,
  enabledFieldSettings: enabledFieldSettingsReducer,
  addUserResponse: addUserReducer,
  userIdentity: userIdentityReducer,
  postSessions: postSessionsReducer,
  addSecondaryEmail: addSecondaryEmailReducer,
  blockOrUnblockUser: blockOrUnblockUserReducer,
  deleteUser: deleteUserReducer,
  unlockUser: unlockUserReducer,
  verify: verifyReducer,
  resetPassword: resetPasswordReducer,
  resetPin: resetPinReducer,
  sendResetPassword: sendResetPasswordReducer,
  resendEmail: resendEmailReducer,
  removeEmailAddress: removeEmailAddressReducer,
  updateUserProfile: updateUserProfileReducer,
  getAssignedRole: getAssignedRoleReducer,
  assignRoles: assignRolesReducer,
  unassignRoles: unassignRolesReducer,
  getCustomObjectData: getCustomObjectDataReducer,
  getCustomObjects: getCustomObjectsReducer,
  getRoleContext: getRoleContextReducer,
  addRoleContext: addRoleContextReducer,
  deleteRoleContext: deleteRoleContextReducer,
  getConsentLogs: getConsentLogsReducer,
  getCustomFields: getCustomFieldsReducer,
  getESFields: getESFieldsReducer,
  getPrivacyPolicyHistory: getPrivacyPolicyHistoryReducer,
  getTwoFactorSettings: getTwoFASettingsReducer,
  deleteMFAFactor: deleteMFAFactorReducer,
  getMFAFactors: getMFAFactorsReducer,
  consumerAuditLogs: consumerAuditLogsReducer,
  consumerAuditLogsByUID: consumerAuditLogsByUIDReducer,
  consumerAuditLogsV1: consumerAuditLogsV1Reducer,
  consumerAuditLogsByUIDV1: consumerAuditLogsByUIDV1Reducer,
  orgnizations: UserOrgs.reducer
});

// Combine schemas
export interface CustomerManagementSchema {
  userBySearchFields: GetSearchUsersSchema;
  users: GetSearchUsersSchema;
  enabledFieldSettings: EnabledFieldSettingsSchema;
  addUserResponse: AddUserSchema;
  userIdentity: GetUserIdentitySchema;
  postSessions: PostSessionsSchema;
  addSecondaryEmail: AddSecondaryEmailSchema;
  blockOrUnblockUser: BlockOrUnblockUserSchema;
  deleteUser: DeleteUserSchema;
  unlockUser: UnlockUserSchema;
  verify: VerifySchema;
  resetPassword: ResetPasswordSchema;
  resetPin: ResetPinSchema;
  sendResetPassword: SendResetPasswordSchema;
  resendEmail: ResendEmailSchema;
  removeEmailAddress: RemoveEmailAddressSchema;
  updateUserProfile: UpdateUserProfileSchema;
  getAssignedRole: GetAssignedRoleSchema;
  assignRoles: AssignRolesSchema;
  unassignRoles: UnassignRolesSchema;
  getCustomObjectData: GetCustomObjectDataSchema;
  getCustomObjects: GetCustomObjectsSchema;
  getRoleContext: GetRoleContextSchema;
  addRoleContext: AddRoleContextSchema;
  deleteRoleContext: DeleteRoleContextSchema;
  getConsentLogs: GetConsentLogsResponse;
  getCustomFields: GetCustomFieldsSchema;
  getESFields: GetESFieldsSchema;
  getPrivacyPolicyHistory: GetPrivacyPolicyHistorySchema;
  getTwoFactorSettings: GetTwoFASettingsSchema;
  deleteMFAFactor: DeleteMFAFactorSchema;
  getMFAFactors: GetMFAFactorsSchema;
  consumerAuditLogs: GetConsumerAuditLogsSchema;
  consumerAuditLogsByUID: GetConsumerAuditLogsSchema;
  consumerAuditLogsV1: GetConsumerAuditLogsV1Schema;
  consumerAuditLogsByUIDV1: GetConsumerAuditLogsV1Schema;
  orgnizations: UserOrgsState;
}

export default customerManagementReducer;
